import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider
} from '@mui/material';
import {
  Chat as ChatIcon,
  Upload as UploadIcon,
  Psychology as AgentIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import LanguageToggle from './LanguageToggle';
import EnhancedChatInterface from './EnhancedChatInterface';
import FileUpload from './FileUpload';
import AgentWorkflow from './AgentWorkflow';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function LayoutApp({ signOut, user }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userName = user?.signInDetails?.loginId || user?.username || t('user.guest');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const tabs = [
    { label: t('nav.chat'), icon: <ChatIcon />, component: <EnhancedChatInterface /> },
    { label: t('nav.upload'), icon: <UploadIcon />, component: <FileUpload /> },
    { label: t('nav.agent'), icon: <AgentIcon />, component: <AgentWorkflow /> }
  ];

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <img 
          src="/images/logo.svg" 
          alt="Logo" 
          style={{ height: '32px', width: 'auto', marginBottom: '8px' }}
        />
        <Typography variant="h6" color="primary" gutterBottom>
          {t('app.title')}
        </Typography>
        <Typography
          variant="caption"
          color="secondary"
          sx={{ 
            fontSize: '0.7rem',
            fontStyle: 'italic',
            display: 'block',
            mb: 1
          }}
        >
          Powered by Groq AI
        </Typography>
        <Divider />
      </Box>
      <List>
        {tabs.map((tab, index) => (
          <ListItem
            component="button"
            key={tab.label}
            onClick={() => handleTabChange(null, index)}
            selected={tabValue === index}
            sx={{
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText'
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main' }}>
              {tab.icon}
            </ListItemIcon>
            <ListItemText primary={tab.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Left side - User info and actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, order: { xs: 2, md: 1 } }}>
            <LanguageToggle />
            <Chip
              label={userName}
              variant="outlined"
              size="small"
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main'
              }}
            />
            {signOut && (
              <IconButton
                color="inherit"
                onClick={signOut}
                title={t('auth.signOut')}
                sx={{ color: 'primary.main' }}
              >
                <LogoutIcon />
              </IconButton>
            )}
          </Box>

          {/* Center - Logo and App title */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            order: { xs: 1, md: 2 },
            flex: { xs: 1, md: 'none' },
            justifyContent: 'center'
          }}>
            <img 
              src="/images/logo.svg" 
              alt="Logo" 
              style={{ height: '40px', width: 'auto' }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h6"
                color="primary"
                sx={{ 
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  mb: 0.5
                }}
              >
                {t('app.title')}
              </Typography>
              <Typography
                variant="caption"
                color="secondary"
                sx={{ 
                  fontSize: '0.75rem',
                  fontStyle: 'italic'
                }}
              >
                Powered by Groq AI
              </Typography>
            </Box>
          </Box>
        </Toolbar>

        {!isMobile && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Container maxWidth="xl">
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="navigation tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={tab.label}
                    icon={tab.icon}
                    label={tab.label}
                    iconPosition="start"
                    id={`simple-tab-${index}`}
                    aria-controls={`simple-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Container>
          </Box>
        )}
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Container 
        maxWidth="xl" 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          py: 2,
          minHeight: 'calc(100vh - 64px)' 
        }}
      >
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={tabValue} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Container>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 2, 
          px: 3, 
          textAlign: 'center',
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          &copy; {new Date().getFullYear()} {t('app.copyright')}
        </Typography>
      </Box>
    </Box>
  );
}

export default LayoutApp;
